import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { demoQueryObjectives } from '../utils/demoData'
import {
  Target,
  Lightbulb,
  GitCompare,
  DollarSign,
  Wrench,
  BookOpen,
  Loader2,
  AlertTriangle,
  ShoppingCart
} from 'lucide-react'

interface QueryObjective {
  objective_slug: string
  objective_name: string
  description: string
  query_count: number
  avg_confidence: number
  your_mentions: number
  percentage: string
  yourMentionRate?: string  // API uses camelCase
  your_mention_rate?: string  // Keep for backwards compatibility
  example_queries?: string[]
}

interface QueryObjectivesProps {
  userBrandId: string
  demoMode?: boolean
}

const OBJECTIVE_ICONS: Record<string, any> = {
  discovery: Lightbulb,
  comparison: GitCompare,
  pricing: DollarSign,
  'problem-solving': Wrench,
  research: BookOpen,
  'purchase-intent': ShoppingCart,
  support: Target
}

const OBJECTIVE_COLORS: Record<string, string> = {
  discovery: '#8B5CF6',      // Purple
  comparison: '#3B82F6',     // Blue
  pricing: '#10B981',        // Green
  'problem-solving': '#F59E0B', // Orange
  research: '#EC4899',       // Pink
  'purchase-intent': '#EF4444', // Red
  support: '#6366F1'         // Indigo
}

export default function QueryObjectives({ userBrandId, demoMode = false }: QueryObjectivesProps) {
  const [objectives, setObjectives] = useState<QueryObjective[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchObjectives()
  }, [userBrandId])

  const fetchObjectives = async () => {
    try {
      setLoading(true)
      setError(null)

      // Use demo data in demo mode without making API call
      if (demoMode) {
        setObjectives(demoQueryObjectives as any)
        setLoading(false)
        return
      }

      const response = await api.get(`/categories/${userBrandId}/objectives`)

      if (response.data.success) {
        setObjectives(response.data.objectives)
      }
    } catch (err: any) {
      console.error('Error fetching objectives:', err)
      setError(err.response?.data?.error || 'Failed to load objectives')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading query objectives...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-2 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (objectives.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Query Data Yet
        </h3>
        <p className="text-gray-600">
          Add and classify queries to see objective breakdown
        </p>
      </div>
    )
  }

  return (
    <div className="">
      <div className="space-y-4">
            {objectives.map((objective) => {
              const Icon = OBJECTIVE_ICONS[objective.objective_slug] || Target
              const color = OBJECTIVE_COLORS[objective.objective_slug] || '#6B7280'
              const queryCount = parseInt(objective.query_count as any) || 0
              const yourMentions = parseInt(objective.your_mentions as any) || 0
              // Calculate mention rate from actual data instead of relying on API string
              const mentionRate = queryCount > 0 ? (yourMentions / queryCount) * 100 : 0

              return (
                <div
                  key={objective.objective_slug}
                  className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-gray-900">
                            {objective.objective_name}
                          </h4>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {objective.percentage}%
                          </span>
                        </div>

                        <p className="text-sm text-gray-600 mb-3">
                          {objective.description}
                        </p>

                        <div className="flex items-center space-x-6">
                          <div>
                            <span className="text-xs text-gray-500">Queries: </span>
                            <span className="text-sm font-semibold text-gray-900">
                              {queryCount}
                            </span>
                          </div>

                          <div>
                            <span className="text-xs text-gray-500">Your Mention Rate: </span>
                            <span
                              className="text-sm font-semibold"
                              style={{
                                color: mentionRate >= 50 ? '#10B981' : mentionRate >= 25 ? '#F59E0B' : '#EF4444'
                              }}
                            >
                              {mentionRate.toFixed(1)}%
                            </span>
                            <span className="text-xs text-gray-500 ml-1">
                              ({yourMentions}/{queryCount})
                            </span>
                          </div>
                        </div>

                        {/* Example Queries */}
                        {objective.example_queries && objective.example_queries.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-2">
                            {objective.example_queries.slice(0, 2).map((query, idx) => (
                                <div key={idx} className="text-[11px] text-gray-600 italic p-1.5 bg-gray-200/25 rounded">
                                  "{query}"
                                </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
      </div>
    </div>
  )
}
